/**
 * Cleaning gate — the supervisor-facing side of the execution gate (CLEAN lane).
 *
 * The gate itself lives on the operation-start path (`batch_config_view.start_operation_manual`)
 * and is fail-closed: equipment with no valid cleaning refuses the start with HTTP 409 and a
 * message that names the equipment, the cleaning class it needs and why. Until now nothing in the
 * admin app called any of it, so the first anyone heard of a refusal was an operator reading a red
 * box on a tablet at 02:00. These three endpoints answer the same question *before* the line stops:
 *
 *   GET /bp/cleaning/outstanding/[?plant_id=]        every machine in the plant that would refuse
 *   GET /bp/cleaning/gate/?batch_number=[&operation_id=]   what the gate would do for ONE batch now
 *   GET /bp/cleaning/requirement/?machine_id=&product_id=  what one machine needs for one product
 *
 * `gate/` and `requirement/` answer **409 with a full body** when the gate would refuse — that is
 * the answer, not an error — so every call here unwraps the axios rejection and returns the body
 * with `httpStatus` attached, exactly as `ackApi` does for its 428. Callers must therefore branch
 * on `data.ok`, never on the promise rejecting.
 */
import Axios from 'axios';

const bp = '/bp';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });

/** Resolves to the response body even on 409/500; never throws. */
const unwrap = (p) =>
  p
    .then((r) => ({ ...(r.data || {}), httpStatus: r.status }))
    .catch((e) => ({
      status: 'failed',
      message: e?.response?.data?.message || e?.message || 'Network error',
      ...(e?.response?.data || {}),
      httpStatus: e?.response?.status || 0,
    }));

/** Every machine whose cleaning state does not satisfy what it would need. `{count, machines:[…]}`. */
export const cleaningOutstanding = (plantId) =>
  unwrap(Axios.get(`${bp}/cleaning/outstanding/`, { params: plantId ? { plant_id: plantId } : {}, ...auth() }));

/** What the execution gate would do for this batch right now. `{ok, message, refusals, machines:[…]}`. */
export const cleaningGate = (batchNumber, operationId) =>
  unwrap(
    Axios.get(`${bp}/cleaning/gate/`, {
      params: { batch_number: batchNumber, ...(operationId ? { operation_id: operationId } : {}) },
      ...auth(),
    })
  );

/** What one machine needs before it may run this product. */
export const cleaningRequirement = (machineId, productId, trigger) =>
  unwrap(
    Axios.get(`${bp}/cleaning/requirement/`, {
      params: { machine_id: machineId, ...(productId ? { product_id: productId } : {}), ...(trigger ? { trigger } : {}) },
      ...auth(),
    })
  );

/** Plant-language label for a cleaning state key (`DIRTY_EXPIRED` → `Dirty — hold expired`). */
export const CLEAN_STATE_LABEL = {
  CLEAN: 'Clean',
  CLEAN_EXPIRED: 'Clean — validity expired',
  CLEAN_FAILED: 'Cleaning failed',
  DIRTY: 'Dirty',
  DIRTY_EXPIRED: 'Dirty — hold exceeded',
  CLEANING_IN_PROGRESS: 'Cleaning in progress',
  AWAITING_APPROVAL: 'Cleaning awaiting approval',
  AWAITING_RESULT: 'Awaiting swab result',
  NEVER_CLEANED: 'Never cleaned',
  OUT_OF_SERVICE: 'Out of service',
};

/** antd Tag colour per cleaning state — red is reserved for states that refuse a start. */
export const CLEAN_STATE_COLOR = {
  CLEAN: 'green',
  CLEAN_EXPIRED: 'red',
  CLEAN_FAILED: 'red',
  DIRTY: 'orange',
  DIRTY_EXPIRED: 'red',
  CLEANING_IN_PROGRESS: 'blue',
  AWAITING_APPROVAL: 'gold',
  AWAITING_RESULT: 'gold',
  NEVER_CLEANED: 'default',
  OUT_OF_SERVICE: 'red',
};

export const cleanStateLabel = (s) => CLEAN_STATE_LABEL[s] || String(s || '—').replace(/_/g, ' ');
export const cleanStateColor = (s) => CLEAN_STATE_COLOR[s] || 'default';

export default { cleaningOutstanding, cleaningGate, cleaningRequirement, cleanStateLabel, cleanStateColor };
